import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, App, Button, DatePicker, Form, Input, InputNumber, Modal, Select } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import type { z } from 'zod'
import type { billDetailSchema } from '@super-mega/server/contracts/bill'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import { RefundForm } from './refund-form.tsx'

type Bill = z.infer<typeof billDetailSchema>
type Values = {
  kind: 'supplement' | 'discount' | 'cancellation' | 'depositRelease' | 'reversal'
  amount: string
  reason: string
  effectiveAt: Dayjs
  reversalOfId?: number
}
export function BillAdjustment({
  bill,
  onClose,
  onSaved
}: {
  bill: Bill
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const trpc = useTRPC(),
    { message } = App.useApp(),
    [key] = useState(() => crypto.randomUUID()),
    [form] = Form.useForm<Values>()
  const amount = Form.useWatch<string | undefined>('amount', form),
    kind: Values['kind'] = Form.useWatch('kind', form)
  const preview = useQuery({
    ...trpc.bill.adjustmentPreview.queryOptions({ billId: bill.id, amount: amount ?? '0' }),
    enabled: !!amount && /^-?\d+(\.\d{1,2})?$/.test(amount) && amount !== '0'
  })
  const save = useMutation(trpc.bill.adjust.mutationOptions())
  return (
    <ModalForm<Values>
      dateFormatter={false}
      form={form}
      open
      title="调整账单金额"
      layout="vertical"
      modalProps={{ onCancel: onClose, destroyOnHidden: true, mask: { closable: false } }}
      initialValues={{ kind: 'discount', effectiveAt: dayjs() }}
      submitter={{
        submitButtonProps: { loading: save.isPending, disabled: !preview.data || preview.isFetching || preview.isError }
      }}
      onFinish={async (v) => {
        try {
          await save.mutateAsync({
            billId: bill.id,
            expectedState: preview.data!.expectedState,
            ...v,
            effectiveAt: v.effectiveAt.toDate(),
            reversalOfId: v.reversalOfId ?? null,
            idempotencyKey: key
          })
          await onSaved()
          return true
        } catch (e) {
          void message.error(e instanceof Error ? e.message : '调整失败')
          void preview.refetch()
          return false
        }
      }}
    >
      <p>
        原始金额 ¥{formatAmount(bill.originalAmount)} · 当前有效金额 ¥{formatAmount(bill.totalAmount)}
      </p>
      <Form.Item name="kind" label="调整类型" rules={[{ required: true }]}>
        <Select
          onChange={() => form.setFieldValue('reversalOfId', undefined)}
          options={[
            { value: 'supplement', label: '增补' },
            { value: 'discount', label: '减免' },
            { value: 'cancellation', label: '取消费用' },
            ...(bill.feeType === 'deposit' ? [{ value: 'depositRelease', label: '押金释放' }] : []),
            { value: 'reversal', label: '冲回原调整' }
          ]}
        />
      </Form.Item>
      {kind === 'reversal' && (
        <Form.Item name="reversalOfId" label="原调整" rules={[{ required: true }]}>
          <Select
            options={bill.adjustments.map((a) => ({ value: a.id, label: `#${a.id} ${a.amount} · ${a.reason}` }))}
          />
        </Form.Item>
      )}
      <Form.Item
        name="amount"
        label="带符号调整额"
        extra="增补为正；减免、取消及押金释放为负。"
        rules={[{ required: true }]}
      >
        <InputNumber stringMode precision={2} className="w-full" />
      </Form.Item>
      <Form.Item name="effectiveAt" label="业务生效时间" rules={[{ required: true }]}>
        <DatePicker showTime className="w-full" />
      </Form.Item>
      <Form.Item name="reason" label="调整原因" rules={[{ required: true, whitespace: true }]}>
        <Input.TextArea maxLength={5000} />
      </Form.Item>
      {preview.isError && <Alert type="error" title={preview.error.message} />}
      {preview.data && (
        <Alert
          type="warning"
          title={`调整后 ¥${formatAmount(preview.data.effective)}，待收付 ¥${formatAmount(preview.data.outstanding)}，待退回 ¥${formatAmount(preview.data.refundable)}`}
          description={
            preview.data.voidGroupIds.length
              ? `以下账单组将作废，需要重新组单：${preview.data.voidGroupIds.join('、')}`
              : '此操作不产生实际资金流水。'
          }
        />
      )}
    </ModalForm>
  )
}
export function BillRefund({
  bill,
  onClose,
  onSaved
}: {
  bill: Bill
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const trpc = useTRPC(),
    query = useQuery(trpc.bill.refundable.queryOptions({ billId: bill.id }))
  let emptyMessage = '没有可退的原收付款分配'
  if (query.isPending) {
    emptyMessage = '正在查询原收付款…'
  } else if (query.isError) {
    emptyMessage = query.error.message
  }
  if (!query.data?.length) {
    return (
      <Modal open title="登记退款" onCancel={onClose} footer={<Button onClick={onClose}>关闭</Button>}>
        <Alert
          type={query.isError ? 'error' : 'info'}
          title={emptyMessage}
          action={query.isError ? <Button onClick={() => void query.refetch()}>重试</Button> : undefined}
        />
      </Modal>
    )
  }
  return <RefundForm bill={bill} sources={query.data} onClose={onClose} onSaved={onSaved} />
}
